import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  Download,
  Trash2,
  Calendar,
  User,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Heart,
  Lightbulb,
  AlertTriangle,
  CheckCircle,
  Edit3,
  Save,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AnalysisHero } from '../components/AnalysisHero';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/SupabaseAuthContext';

// Biomarker units mapping
// Using ASCII-safe representations for consistency with PDF
const BIOMARKER_UNITS = {
  // PhenoAge biomarkers
  albumin: 'g/dL',
  creatinine: 'mg/dL',
  glucose: 'mg/dL',
  hs_crp: 'mg/L',
  lymphocyte_percent: '%',
  rdw: '%',
  wbc: 'x10^3/uL',
  alp: 'U/L',
  mcv: 'fL',
  
  // Bortz Age biomarkers
  cystatin_c: 'mg/L',
  monocyte_absolute: 'x10^3/uL',
  shbg: 'nmol/L',
  ggt: 'U/L',
  vitamin_d: 'ng/mL',
  rbc: 'x10^6/uL',
  apolipoprotein_a1: 'mg/dL',
  alt: 'U/L',
  cholesterol_total: 'mg/dL',
  hba1c: '%',
  neutrophil_absolute: 'x10^3/uL',
  
  // Additional common biomarkers
  hemoglobin: 'g/dL',
  hematocrit: '%',
  platelet_count: 'x10^3/uL',
  triglycerides: 'mg/dL',
  ldl_cholesterol: 'mg/dL',
  hdl_cholesterol: 'mg/dL',
  ast: 'U/L',
  bun: 'mg/dL',
  sodium: 'mEq/L',
  potassium: 'mEq/L',
  calcium: 'mg/dL',
  phosphorus: 'mg/dL',
  uric_acid: 'mg/dL',
  iron: 'ug/dL',
  ferritin: 'ng/mL',
  transferrin: 'mg/dL',
  tibc: 'ug/dL',
  b12: 'pg/mL',
  folate: 'ng/mL',
  tsh: 'mIU/L',
  t3: 'ng/dL',
  t4: 'ug/dL',
  free_t4: 'ng/dL',
  insulin: 'uIU/mL',
  cortisol: 'ug/dL',
  testosterone: 'ng/dL',
  estradiol: 'pg/mL',
  progesterone: 'ng/mL',
  dhea_s: 'ug/dL',
  igf_1: 'ng/mL',
  homocysteine: 'umol/L',
  fibrinogen: 'mg/dL',
  d_dimer: 'ng/mL',
  esr: 'mm/hr',
  egfr: 'mL/min/1.73m2',
};

const getBiomarkerUnit = (key) => BIOMARKER_UNITS[key] || '';

// Canonical analyte codes (as returned in missing_*age_fields) -> concise labels.
const CANONICAL_LABELS = {
  albumin: 'Albumin',
  creatinine: 'Creatinine',
  glucose: 'Glucose',
  c_reactive_protein: 'hs-CRP',
  lymphocytes_percent: 'Lymphocyte %',
  mean_corpuscular_volume: 'MCV',
  red_cell_distribution_width: 'RDW',
  alkaline_phosphatase: 'ALP',
  white_blood_cells: 'WBC',
  urea: 'BUN',
  cholesterol_total: 'Cholesterol',
  cystatin_c: 'Cystatin C',
  hba1c: 'HbA1c',
  gamma_glutamyl_transferase: 'GGT',
  red_blood_cells: 'RBC',
  monocytes_absolute: 'Monocyte Abs',
  neutrophils_absolute: 'Neutrophil Abs',
  alanine_aminotransferase: 'ALT',
  sex_hormone_binding_globulin: 'SHBG',
  vitamin_d_25oh: 'Vitamin D',
  mean_corpuscular_hemoglobin: 'MCH',
  apolipoprotein_a1: 'ApoA1',
};

const labelForCode = (code) =>
  CANONICAL_LABELS[code] ||
  code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Canonical analyte code -> the legacy blood_values grid key it maps to. Lets us
// tie a missing/invalid required marker (reported in canonical codes) back to the
// exact cell in the extracted-values grid so we can highlight it.
const CANONICAL_TO_LEGACY = {
  albumin: 'albumin',
  creatinine: 'creatinine',
  glucose: 'glucose',
  c_reactive_protein: 'hs_crp',
  lymphocytes_percent: 'lymphocyte_percent',
  mean_corpuscular_volume: 'mcv',
  red_cell_distribution_width: 'rdw',
  alkaline_phosphatase: 'alp',
  white_blood_cells: 'wbc',
  urea: 'bun',
  cholesterol_total: 'cholesterol_total',
  cystatin_c: 'cystatin_c',
  hba1c: 'hba1c',
  gamma_glutamyl_transferase: 'ggt',
  red_blood_cells: 'rbc',
  monocytes_absolute: 'monocyte_absolute',
  neutrophils_absolute: 'neutrophil_absolute',
  alanine_aminotransferase: 'alt',
  sex_hormone_binding_globulin: 'shbg',
  vitamin_d_25oh: 'vitamin_d',
  mean_corpuscular_hemoglobin: 'mch',
  apolipoprotein_a1: 'apolipoprotein_a1',
};

// Human label for a legacy grid key (reverses the canonical map for nice names).
const LEGACY_TO_CANONICAL = Object.fromEntries(
  Object.entries(CANONICAL_TO_LEGACY).map(([canon, legacy]) => [legacy, canon])
);
const labelForLegacyKey = (key) =>
  (LEGACY_TO_CANONICAL[key] && CANONICAL_LABELS[LEGACY_TO_CANONICAL[key]]) ||
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// The 22 legacy blood_values keys the clocks consume. Used to seed edit mode so
// markers missing from extraction still get a blank input the user can fill.
const BIOAGE_LEGACY_KEYS = [
  'albumin', 'creatinine', 'glucose', 'hs_crp', 'lymphocyte_percent', 'mcv',
  'rdw', 'alp', 'wbc', 'bun', 'cholesterol_total', 'cystatin_c', 'hba1c',
  'ggt', 'rbc', 'monocyte_absolute', 'neutrophil_absolute', 'alt', 'shbg',
  'vitamin_d', 'mch', 'apolipoprotein_a1',
];

const seedEditableValues = (bloodValues) => {
  const seeded = {};
  BIOAGE_LEGACY_KEYS.forEach((k) => { seeded[k] = null; });
  return { ...seeded, ...(bloodValues || {}) };
};

export const AnalysisResultPage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDemo } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [patient, setPatient] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [editedCollectionDate, setEditedCollectionDate] = useState('');
  const [saving, setSaving] = useState(false);
  // Conditional review: populated from the extraction summary passed by the
  // upload page when a marker was missing or read with low confidence.
  const [reviewItems, setReviewItems] = useState([]);
  const [flaggedKeys, setFlaggedKeys] = useState(() => new Set());
  const [reviewApplied, setReviewApplied] = useState(false);

  // Required markers that block a clock, keyed by their legacy grid key. Derived
  // from the strict engine's completeness report (missing OR out-of-range/invalid
  // inputs, in canonical codes). `hasValue` distinguishes a marker that is simply
  // absent (needs adding) from one that IS present but implausible (needs a units
  // check) — the latter is how the RDW-SD-vs-RDW-CV mistake surfaces.
  const requiredIssues = useMemo(() => {
    const map = new Map();
    if (!analysis) return map;
    const add = (codes, clock) => {
      (codes || []).forEach((code) => {
        const legacy = CANONICAL_TO_LEGACY[code] || code;
        const entry = map.get(legacy) || { clocks: new Set(), canonical: code };
        entry.clocks.add(clock);
        map.set(legacy, entry);
      });
    };
    add(analysis.missing_phenoage_fields, 'PhenoAge');
    add(analysis.missing_bortzage_fields, 'Bortz');
    map.forEach((entry, legacy) => {
      const v = analysis.blood_values?.[legacy];
      entry.hasValue = v !== null && v !== undefined && v !== '';
    });
    return map;
  }, [analysis]);

  // View-mode grid source: existing readings PLUS a blank slot for any required
  // marker that was never extracted, so missing markers still render (in red) and
  // can be spotted at a glance.
  const viewEntries = useMemo(() => {
    const base = { ...(analysis?.blood_values || {}) };
    requiredIssues.forEach((_entry, legacy) => {
      if (!(legacy in base)) base[legacy] = null;
    });
    return base;
  }, [analysis, requiredIssues]);

  const fetchRecommendations = useCallback(async () => {
    try {
      const response = await apiFetch(`/analyses/${analysisId}/recommendations`);
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  }, [analysisId]);

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await apiFetch(`/analyses/${analysisId}`);
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        
        // Fetch patient details
        const patientResponse = await apiFetch(`/patients/${data.patient_id}`);
        if (patientResponse.ok) {
          const patientData = await patientResponse.json();
          setPatient(patientData);
        }
      } else {
        toast.error('Analysis not found');
        navigate('/analyses');
      }
    } catch (error) {
      console.error('Failed to fetch analysis:', error);
      toast.error('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  }, [analysisId, navigate]);

  useEffect(() => {
    fetchAnalysis();
    fetchRecommendations();
  }, [fetchAnalysis, fetchRecommendations]);

  // Conditional review: if the upload flow flagged this analysis (a required
  // marker was missing or a value was read with low confidence), auto-open edit
  // mode once, pre-fill any missing required markers, and highlight the fields
  // that need a human glance. High-confidence complete uploads skip all of this.
  useEffect(() => {
    if (reviewApplied || !analysis) return;
    const extraction = location.state?.extraction;
    if (!extraction || !extraction.recommend_review) return;

    const humanize = (key) =>
      key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    const missing = extraction.missing_phenoage || [];
    const lowConf = extraction.low_confidence || [];
    const flagged = new Set([...missing, ...lowConf]);

    const items = [
      ...missing.map((k) => ({ key: k, label: humanize(k), reason: 'not found' })),
      ...lowConf
        .filter((k) => !missing.includes(k))
        .map((k) => ({ key: k, label: humanize(k), reason: 'low confidence' })),
    ];

    // Seed all bio-age fields (existing readings + blanks for anything missing)
    // so the reviewer can fill gaps for either clock without leaving the page.
    setEditedValues(seedEditableValues(analysis.blood_values));
    setEditedCollectionDate(analysis.collection_date);
    setEditMode(true);
    setReviewItems(items);
    setFlaggedKeys(flagged);
    setReviewApplied(true);
  }, [analysis, location.state, reviewApplied]);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const response = await apiFetch(`/report/${analysisId}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pranagen_report_${patient?.patient_id_number || 'unknown'}_${analysis.collection_date}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report downloaded');
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await apiFetch(`/analyses/${analysisId}/recalculate`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const updatedAnalysis = await response.json();
        setAnalysis(updatedAnalysis);
        toast.success('Biological ages recalculated');
      } else {
        toast.error('Failed to recalculate');
      }
    } catch (error) {
      toast.error('Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await apiFetch(`/analyses/${analysisId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        toast.success('Analysis deleted');
        navigate('/analyses');
      } else {
        toast.error('Failed to delete analysis');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
    setShowDeleteDialog(false);
  };

  const handleStartEdit = () => {
    setEditedValues(seedEditableValues(analysis.blood_values));
    setEditedCollectionDate(analysis.collection_date);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedValues({});
    setEditedCollectionDate('');
    setReviewItems([]);
    setFlaggedKeys(new Set());
  };

  const handleValueChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value === '' ? null : parseFloat(value) || value
    }));
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await apiFetch(`/analyses/${analysisId}/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blood_values: editedValues,
          collection_date: editedCollectionDate
        }),
      });
      
      if (response.ok) {
        const updatedAnalysis = await response.json();
        setAnalysis(updatedAnalysis);
        setEditMode(false);
        setEditedValues({});
        setReviewItems([]);
        setFlaggedKeys(new Set());
        toast.success('Values updated and ages recalculated');
        // Refresh recommendations
        fetchRecommendations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update values');
      }
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      // Parse date string directly to avoid timezone issues
      // dateStr can be "YYYY-MM-DD" or ISO datetime string
      if (!dateStr) return '';
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getAgeDiff = (bioAge, chronoAge) => {
    if (!bioAge || !chronoAge) return null;
    return (bioAge - chronoAge).toFixed(1);
  };

  const getAgeStatus = (bioAge, chronoAge) => {
    const diff = bioAge - chronoAge;
    if (diff <= -2) return { label: 'Younger', color: 'bg-green-100 text-green-800', icon: TrendingDown };
    if (diff >= 2) return { label: 'Older', color: 'bg-red-100 text-red-800', icon: TrendingUp };
    return { label: 'On Track', color: 'bg-[#dbe9e5] text-[#196a70]', icon: Minus };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  // Check if any calculations are missing
  const needsRecalculation = !analysis.pheno_age && !analysis.bortz_age;

  return (
    <div className="space-y-6" data-testid="analysis-result">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Analysis Results
            </h1>
            <p className="text-slate-600 text-sm">
              {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient'} • {formatDate(analysis.collection_date)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!isDemo && needsRecalculation && (
            <Button
              variant="outline"
              onClick={handleRecalculate}
              disabled={recalculating}
              className="text-[#196a70] border-[#196a70]/30 hover:bg-[#dbe9e5]"
              data-testid="recalculate-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
              {recalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
          )}
          {!isDemo && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="delete-analysis-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          <Button
            onClick={handleDownloadReport}
            disabled={downloading}
            className="bg-[#196a70] hover:bg-[#12565b] text-white"
            data-testid="download-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? 'Generating...' : 'Download Report'}
          </Button>
        </div>
      </div>

      {/* Patient Info Card */}
      <Card className="bg-white border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <User className="w-5 h-5 text-[#196a70]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Patient</p>
                <p className="font-medium text-slate-900">
                  {patient ? `${patient.first_name} ${patient.last_name}` : '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <span className="text-[#196a70] font-mono text-sm font-bold">ID</span>
              </div>
              <div>
                <p className="text-xs text-slate-500">Patient ID</p>
                <p className="font-medium text-slate-900 font-mono">
                  {patient?.patient_id_number || '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#196a70]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Collection Date</p>
                <p className="font-medium text-slate-900">
                  {formatDate(analysis.collection_date)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#196a70]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Chronological Age</p>
                <p className="font-medium text-slate-900">
                  {analysis.chronological_age?.toFixed(1)} years
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BioAge Hero - Main Visual */}
      <AnalysisHero analysis={analysis} />

      {/* Age Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* PhenoAge */}
        <Card className="bg-white border-slate-200 shadow-soft card-hover">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-500">
                PhenoAge
              </CardTitle>
              {analysis.pheno_age && (
                <Badge className={getAgeStatus(analysis.pheno_age, analysis.chronological_age).color}>
                  {(() => {
                    const status = getAgeStatus(analysis.pheno_age, analysis.chronological_age);
                    const Icon = status.icon;
                    return (
                      <>
                        <Icon className="w-3 h-3 mr-1" />
                        {status.label}
                      </>
                    );
                  })()}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-4xl font-bold font-heading text-slate-900">
                {analysis.pheno_age?.toFixed(1) || '—'}
                <span className="text-lg font-normal text-slate-400 ml-1">years</span>
              </p>
              {analysis.pheno_age ? (
                <p className="text-sm text-slate-600">
                  {getAgeDiff(analysis.pheno_age, analysis.chronological_age) > 0 ? '+' : ''}
                  {getAgeDiff(analysis.pheno_age, analysis.chronological_age)} years from chronological
                </p>
              ) : (
                <div className="text-xs text-red-700" data-testid="phenoage-missing">
                  {analysis.missing_phenoage_fields?.length > 0 ? (
                    <>
                      <span className="font-medium">
                        PhenoAge needs {analysis.missing_phenoage_fields.length} value{analysis.missing_phenoage_fields.length > 1 ? 's' : ''} resolved:
                      </span>{' '}
                      {analysis.missing_phenoage_fields.map(labelForCode).join(', ')}.
                      <span className="block text-slate-500 mt-1">
                        See the highlighted fields below — add or fix them in “Edit Values”.
                      </span>
                    </>
                  ) : (
                    'Requires: Albumin, Creatinine, Glucose, hs-CRP, Lymphocyte %, MCV, RDW, ALP, WBC.'
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bortz Age */}
        <Card className="bg-white border-slate-200 shadow-soft card-hover">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-500">
                Bortz Age
              </CardTitle>
              {analysis.bortz_age && (
                <Badge className={getAgeStatus(analysis.bortz_age, analysis.chronological_age).color}>
                  {(() => {
                    const status = getAgeStatus(analysis.bortz_age, analysis.chronological_age);
                    const Icon = status.icon;
                    return (
                      <>
                        <Icon className="w-3 h-3 mr-1" />
                        {status.label}
                      </>
                    );
                  })()}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-4xl font-bold font-heading text-slate-900">
                {analysis.bortz_age?.toFixed(1) || '—'}
                <span className="text-lg font-normal text-slate-400 ml-1">years</span>
              </p>
              {analysis.bortz_age ? (
                <p className="text-sm text-slate-600">
                  {getAgeDiff(analysis.bortz_age, analysis.chronological_age) > 0 ? '+' : ''}
                  {getAgeDiff(analysis.bortz_age, analysis.chronological_age)} years from chronological
                </p>
              ) : (
                <div className="text-xs text-red-700" data-testid="bortzage-missing">
                  {analysis.missing_bortzage_fields?.length > 0 ? (
                    <>
                      <span className="font-medium">
                        Bortz Age needs {analysis.missing_bortzage_fields.length} value{analysis.missing_bortzage_fields.length > 1 ? 's' : ''} resolved:
                      </span>{' '}
                      {analysis.missing_bortzage_fields.map(labelForCode).join(', ')}.
                      <span className="block text-slate-500 mt-1">
                        See the highlighted fields below — add or fix them in “Edit Values”.
                      </span>
                    </>
                  ) : (
                    'Requires all 21 Bortz biomarkers.'
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mortality Risk Card */}
      {analysis.mortality_risk !== null && analysis.mortality_risk !== undefined && (
        <Card className="bg-white border-slate-200 shadow-soft">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">
              Estimated 10-Year Mortality Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <div 
                  className={`text-5xl font-bold font-heading ${
                    analysis.mortality_risk < 5 ? 'text-green-600' : 
                    analysis.mortality_risk < 15 ? 'text-amber-500' : 'text-red-600'
                  }`}
                >
                  {analysis.mortality_risk}%
                </div>
                <Badge 
                  className={`mt-2 ${
                    analysis.mortality_risk < 5 ? 'bg-green-100 text-green-800' : 
                    analysis.mortality_risk < 15 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {analysis.mortality_risk < 5 ? 'Low Risk' : 
                   analysis.mortality_risk < 15 ? 'Moderate Risk' : 'Elevated Risk'}
                </Badge>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-600">
                  Based on PhenoAge biomarker analysis, this represents the estimated probability 
                  of mortality within the next 10 years. This metric is derived from validated 
                  clinical algorithms using blood biomarker patterns.
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Note: This is a statistical estimate and not a definitive prediction. 
                  Individual outcomes depend on many factors including lifestyle, treatment, and genetics.
                </p>
              </div>
            </div>
            {/* Risk scale with position marker */}
            {(() => {
              const r = analysis.mortality_risk;
              let pos;
              if (r < 5) pos = (r / 5) * 33.33;
              else if (r < 15) pos = 33.33 + ((r - 5) / 10) * 33.33;
              else pos = 66.66 + Math.min((r - 15) / 15, 1) * 33.34;
              pos = Math.max(1.5, Math.min(98.5, pos));
              return (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="relative pt-6">
                    {/* marker */}
                    <div
                      className="absolute top-0 flex flex-col items-center -translate-x-1/2 transition-all"
                      style={{ left: `${pos}%` }}
                    >
                      <span className="text-[11px] font-semibold font-mono text-slate-700 mb-0.5">{r}%</span>
                      <div className="w-3 h-3 rotate-45 bg-[#0F3D4E] rounded-[2px] shadow-sm" />
                    </div>
                    <div
                      className="h-2.5 rounded-full"
                      style={{ background: 'linear-gradient(90deg,#7FC4CC 0 33%,#2f8d91 33% 66%,#153d43 66%)' }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-[11px] text-slate-400">
                    <span>Low &lt;5%</span>
                    <span>Moderate 5–15%</span>
                    <span>Elevated ≥15%</span>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Health Recommendations Section */}
      {recommendations && (recommendations.age_clock_recommendations?.length > 0 || recommendations.recommendations?.length > 0 || recommendations.general_recommendations?.length > 0) && (
        <Card className="bg-white border-slate-200 shadow-soft">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#7a63b8]" />
              <CardTitle className="font-heading text-lg">Personalized Health Recommendations</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Age Clock-Based Recommendations (Top 3 by Formula Weight) */}
            {recommendations.age_clock_recommendations?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#7a63b8]" />
                  Top 3 Interventions to Reduce Biological Age
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Based on weighted analysis of PhenoAge and Bortz Age formulas, these interventions would have the greatest impact on your biological age.
                </p>
                <div className="space-y-4">
                  {recommendations.age_clock_recommendations.map((rec, index) => (
                    <div key={index} className="border border-[#ddd4ee] rounded-lg p-4 bg-gradient-to-r from-[#efeaf8] to-[#f5f4fb]">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#5b4595] text-white text-sm font-bold">
                            {index + 1}
                          </span>
                          <h4 className="font-semibold text-slate-900">{rec.title}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-[#ddd4ee] text-[#5b4595] bg-white text-xs">
                            {rec.clock}
                          </Badge>
                          {rec.estimated_years_reduction > 0 && (
                            <Badge className="bg-[#2f9b72] text-white font-bold">
                              -{rec.estimated_years_reduction} years
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 flex items-center gap-4">
                        <span className="text-xs text-slate-500">
                          Current: <span className="font-medium text-slate-700">{rec.current_value} {rec.unit}</span>
                          {' → '}
                          Optimal: <span className="font-medium text-[#1f7355]">{rec.optimal_value} {rec.unit}</span>
                        </span>
                        {(rec.phenoage_years > 0 || rec.bortz_years > 0) && (
                          <span className="text-xs text-[#5b4595]">
                            {rec.phenoage_years > 0 && `PhenoAge: -${rec.phenoage_years}y`}
                            {rec.phenoage_years > 0 && rec.bortz_years > 0 && ' | '}
                            {rec.bortz_years > 0 && `Bortz: -${rec.bortz_years}y`}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{rec.explanation}</p>
                      <div className="bg-white/60 rounded-md p-3">
                        <p className="text-xs font-medium text-slate-700 mb-2">Action Steps:</p>
                        <ul className="space-y-1.5">
                          {rec.actions.slice(0, 4).map((action, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-[#2f9b72] mt-0.5 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Total Potential Reduction */}
                {recommendations.age_clock_recommendations.length > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-[#e8f5ee] to-[#eef6f0] border border-[#c2e3d1] rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[#1f7355]">Total Potential Age Reduction</p>
                        <p className="text-xs text-[#2f9b72]">If all top 3 interventions achieve optimal values</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-[#1f7355]">
                          -{recommendations.age_clock_recommendations.reduce((sum, r) => sum + (r.estimated_years_reduction || 0), 0).toFixed(1)} years
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Biomarker-specific recommendations */}
            {recommendations.recommendations?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#227c8b]" />
                  Additional Areas Based on Blood Levels
                </h3>
                <div className="space-y-4">
                  {recommendations.recommendations.map((rec, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-slate-900">{rec.title}</h4>
                        <Badge variant="outline" className={
                          rec.status === 'high' ? 'border-[#a9564c]/45 text-[#a9564c]' : 'border-[#196a70]/35 text-[#196a70]'
                        }>
                          {rec.value} {rec.unit} (Optimal: {rec.optimal_range})
                        </Badge>
                      </div>
                      <ul className="space-y-2 mt-3">
                        {rec.recommendations.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-[#2f9b72] mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* General recommendations */}
            {recommendations.general_recommendations?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#2f9b72]" />
                  General Wellness Recommendations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendations.general_recommendations.map((rec, index) => (
                    <div key={index} className="p-4 bg-gradient-to-br from-[#e8f5ee] to-[#eef6f1] rounded-lg border border-[#c2e3d1]">
                      <h4 className="font-medium text-[#1f7355] mb-2">{rec.title}</h4>
                      <p className="text-sm text-slate-600">{rec.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 mt-6 italic">
              These recommendations are for informational purposes only and should not replace professional medical advice. 
              Please consult with your healthcare provider before making significant changes to your diet, exercise, or treatment regimen.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Blood Values - Editable */}
      {((analysis.blood_values && Object.keys(analysis.blood_values).length > 0) || requiredIssues.size > 0) && (
        <Card className="bg-white border-slate-200 shadow-soft">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Extracted Blood Values</CardTitle>
              {isDemo ? null : !editMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  className="text-[#196a70] border-[#196a70]"
                  data-testid="edit-values-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit Values
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="bg-[#196a70] text-white hover:bg-[#004d87]"
                    data-testid="save-values-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save & Recalculate'}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {reviewItems.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="review-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      Please confirm {reviewItems.length} value{reviewItems.length > 1 ? 's' : ''} before this analysis is final
                    </p>
                    <p className="text-sm text-amber-800 mt-1">
                      We read the rest of this report with high confidence. The highlighted
                      field{reviewItems.length > 1 ? 's' : ''} below need a quick check:
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {reviewItems.map((item) => (
                        <li
                          key={item.key}
                          className="text-xs bg-white border border-amber-300 rounded px-2 py-1 text-amber-900"
                        >
                          <span className="font-medium">{item.label}</span>
                          <span className="text-amber-600"> — {item.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {requiredIssues.size > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 rounded-lg" data-testid="required-markers-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">
                      {requiredIssues.size} marker{requiredIssues.size > 1 ? 's' : ''} still needed for a complete result
                    </p>
                    <p className="text-sm text-red-800 mt-1">
                      The <span className="font-medium">red</span> fields below are required by PhenoAge and/or Bortz.
                      A field marked <span className="font-medium">“add”</span> is missing; one marked{' '}
                      <span className="font-medium">“check units”</span> has a value that looks out of range
                      (often the wrong unit, e.g. RDW-SD in fL instead of RDW-CV %).
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {[...requiredIssues.entries()].map(([key, issue]) => (
                        <li
                          key={key}
                          className="text-xs bg-white border border-red-300 rounded px-2 py-1 text-red-900"
                        >
                          <span className="font-medium">{labelForLegacyKey(key)}</span>
                          <span className="text-red-500">
                            {' '}— {issue.hasValue ? 'check units' : 'add'} · {[...issue.clocks].join(' + ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {!editMode && (
                      <Button
                        size="sm"
                        onClick={handleStartEdit}
                        className="mt-3 bg-red-600 text-white hover:bg-red-700"
                        data-testid="fix-markers-btn"
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Add / fix values
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
            {editMode && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Edit Mode:</strong> Modify values below and click "Save & Recalculate" to update biological ages. 
                  Make sure values are in the correct units shown.
                </p>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-amber-800 mb-1">Collection Date</label>
                  <Input
                    type="date"
                    value={editedCollectionDate}
                    onChange={(e) => setEditedCollectionDate(e.target.value)}
                    className="w-48 bg-white"
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Object.entries(editMode ? editedValues : viewEntries).map(([key, value]) => {
                const issue = requiredIssues.get(key);
                // Hide empty non-required markers in view mode; keep required ones
                // visible (in red) so gaps are obvious.
                if (!editMode && (value === null || value === undefined) && !issue) return null;
                const unit = getBiomarkerUnit(key);
                const flagged = flaggedKeys.has(key);
                const wrapperClass = issue
                  ? 'bg-red-50 border-2 border-red-400 ring-1 ring-red-200'
                  : flagged
                    ? 'bg-amber-50 border-2 border-amber-400 ring-1 ring-amber-300'
                    : editMode
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-slate-50';
                return (
                  <div key={key} className={`p-3 rounded-lg ${wrapperClass}`}>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1 flex-wrap">
                      {key.replace(/_/g, ' ')}
                      {issue && (
                        <span className="normal-case text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1">
                          {issue.hasValue ? 'check units' : 'add'} · {[...issue.clocks].join(' + ')}
                        </span>
                      )}
                      {!issue && flagged && (
                        <span className="normal-case text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1">
                          check
                        </span>
                      )}
                    </p>
                    {editMode ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={value ?? ''}
                          onChange={(e) => handleValueChange(key, e.target.value)}
                          className={`h-8 text-sm font-mono bg-white ${issue ? 'border-red-400' : flagged ? 'border-amber-400' : ''}`}
                          placeholder="—"
                          autoFocus={flagged}
                        />
                        {unit && <span className="text-xs text-slate-500 whitespace-nowrap">{unit}</span>}
                      </div>
                    ) : (
                      <p className={`font-mono font-medium ${issue && !issue.hasValue ? 'text-red-400' : 'text-slate-900'}`}>
                        {(value === null || value === undefined) ? '—' : value}
                        {unit && value !== null && value !== undefined && <span className="text-xs text-slate-500 ml-1">{unit}</span>}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card className="bg-gradient-to-br from-white to-[#dbe9e5] border-slate-200 shadow-soft">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-semibold text-slate-900">
                View Patient History
              </h3>
              <p className="text-sm text-slate-600">
                Track biological age trends over time for this patient.
              </p>
            </div>
            <Link to={`/patients/${analysis.patient_id}/history`}>
              <Button
                variant="outline"
                className="border-[#196a70] text-[#196a70] hover:bg-[#196a70] hover:text-white"
                data-testid="view-history-btn"
              >
                View History
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Analysis</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this analysis? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
